import { terraContract } from '@terra/shared-authorization';
import {
  AiAccountDeleteResponseSchema,
  AiAccountModelListSchema,
  AiAccountModelParamsSchema,
  AiAccountModelWriteSchema,
  AiAccountIdParamsSchema,
  AiAccountProbeResultSchema,
  TerraAiAccountListSchema,
  TerraAiAccountSchema,
  TerraAiAccountWriteSchema,
} from '@terra/shared-contracts-ai-accounts';
import { z } from 'zod';
import {
  COCKPIT_ADMIN_PERMISSION,
  COCKPIT_VIEWER_PERMISSION,
} from './cockpit-permissions';

export const cockpitAiAccountRoutes = {
  listTerraAiAccounts: terraContract
    .meta({ permissions: [COCKPIT_VIEWER_PERMISSION] })
    .route({
      method: 'GET',
      path: '/terra-ai-accounts',
      summary: 'List Terra-owned AI accounts',
      inputStructure: 'detailed',
    })
    .output(TerraAiAccountListSchema),
  getTerraAiAccount: terraContract
    .meta({ permissions: [COCKPIT_VIEWER_PERMISSION] })
    .route({
      method: 'GET',
      path: '/terra-ai-accounts/{id}',
      summary: 'Get a Terra-owned AI account',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: AiAccountIdParamsSchema }))
    .output(TerraAiAccountSchema),
  createTerraAiAccount: terraContract
    .meta({ permissions: [COCKPIT_ADMIN_PERMISSION] })
    .route({
      method: 'POST',
      path: '/terra-ai-accounts',
      summary: 'Create a Terra-owned AI account',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: TerraAiAccountWriteSchema }))
    .output(TerraAiAccountSchema),
  updateTerraAiAccount: terraContract
    .meta({ permissions: [COCKPIT_ADMIN_PERMISSION] })
    .route({
      method: 'PUT',
      path: '/terra-ai-accounts/{id}',
      summary: 'Replace a Terra-owned AI account',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        body: TerraAiAccountWriteSchema,
        params: AiAccountIdParamsSchema,
      }),
    )
    .output(TerraAiAccountSchema),
  deleteTerraAiAccount: terraContract
    .meta({ permissions: [COCKPIT_ADMIN_PERMISSION] })
    .route({
      method: 'DELETE',
      path: '/terra-ai-accounts/{id}',
      summary: 'Delete a Terra-owned AI account',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: AiAccountIdParamsSchema }))
    .output(AiAccountDeleteResponseSchema),
  probeTerraAiAccount: terraContract
    .meta({ permissions: [COCKPIT_ADMIN_PERMISSION] })
    .route({
      method: 'POST',
      path: '/terra-ai-accounts/{id}/probe',
      summary: 'Test the connection for a Terra-owned AI account',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: AiAccountIdParamsSchema }))
    .output(AiAccountProbeResultSchema),
  listTerraAiAccountModels: terraContract
    .meta({ permissions: [COCKPIT_VIEWER_PERMISSION] })
    .route({
      method: 'GET',
      path: '/terra-ai-accounts/{id}/models',
      summary: 'List the models an AI account offers',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: AiAccountIdParamsSchema }))
    .output(AiAccountModelListSchema),
  upsertTerraAiAccountModel: terraContract
    .meta({ permissions: [COCKPIT_ADMIN_PERMISSION] })
    .route({
      method: 'PUT',
      path: '/terra-ai-accounts/{id}/models',
      summary: 'Offer a model on an AI account',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        body: AiAccountModelWriteSchema,
        params: AiAccountIdParamsSchema,
      }),
    )
    .output(AiAccountModelListSchema),
  removeTerraAiAccountModel: terraContract
    .meta({ permissions: [COCKPIT_ADMIN_PERMISSION] })
    .route({
      method: 'DELETE',
      path: '/terra-ai-accounts/{id}/models/{modelName}',
      summary: 'Stop offering a model on an AI account',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: AiAccountModelParamsSchema }))
    .output(AiAccountModelListSchema),
  setTerraAiAccountModelEnabled: terraContract
    .meta({ permissions: [COCKPIT_ADMIN_PERMISSION] })
    .route({
      method: 'POST',
      path: '/terra-ai-accounts/{id}/models/{modelName}/enabled',
      summary: 'Enable a model after proving the account can reach it',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        body: z.object({ enabled: z.boolean() }).strict(),
        params: AiAccountModelParamsSchema,
      }),
    )
    .output(AiAccountModelListSchema),
};
